import {
  type CSSProperties,
  type FC,
  type MutableRefObject,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import type { TreeNode } from '../types/findingHierarchy';
import { degreeColor } from '../utils/degreeColors';
import { isAllNormalActive } from '../utils/selectionHelpers';
import styles from './FindingGridPicker.module.css';

interface FindingGridPickerProps {
  visibleOptions: TreeNode[];
  /** Currently focused pill (driven by the hook's keyboard handler). */
  gridRow: number;
  gridCol: number;
  /** Hover updates the keyboard cursor so mouse + keyboard share state. */
  setGridPos: (row: number, col: number) => void;
  /** Statements already inserted upstream. A pill renders selected when its
   *  leaf `displayValue` exact-matches one of these. */
  selectedStatements?: string[];
  /** Toggle a pill. The hook resolves the full (unfiltered) row + class from the
   *  leaf and `classFolderId` so selection stays correct under an active search.
   *  classFolderId is undefined for the single-class (flat) shape. */
  onSelectLeaf: (leaf: TreeNode, classFolderId?: string) => void;
  /** Toggle a whole finding class to/from its collapsed "all normal" summary. */
  onToggleAllNormal: (classFolderId?: string) => void;
  /** Written on every render so the hook can peek at the currently-focused
   *  pill's DOM node for wrap-aware Down/Up navigation. */
  focusedPillDomRef?: MutableRefObject<HTMLButtonElement | null>;
}

interface AllNormalButtonProps {
  active: boolean;
  /** This button occupies its own nav-grid row (col 0); focused when the
   *  keyboard/hover cursor points at it. */
  focused: boolean;
  rowIndex: number;
  setGridPos: (row: number, col: number) => void;
  onToggle: () => void;
  focusedPillRef: MutableRefObject<HTMLButtonElement | null>;
}

/** Class-level "all normal" toggle. Visually distinct from the per-statement
 *  pills so it reads as applying to the whole class. Rendered in the same row
 *  grid (empty label cell) so it lines up with the pill columns, with a 1px
 *  divider below separating it from the options. Navigable by the arrow keys
 *  as its own grid row. */
function AllNormalButton({
  active,
  focused,
  rowIndex,
  setGridPos,
  onToggle,
  focusedPillRef,
}: AllNormalButtonProps) {
  const classes = [
    styles.allNormal,
    active && styles.allNormalActive,
    focused && styles.allNormalFocused,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={`${styles.row} ${styles.allNormalRow}`}>
      <span className={styles.rowLabel} aria-hidden="true" />
      <div className={styles.pills}>
        <button
          type="button"
          ref={focused ? focusedPillRef : undefined}
          className={classes}
          aria-pressed={active}
          onMouseEnter={() => setGridPos(rowIndex, 0)}
          onClick={onToggle}
        >
          All normal
        </button>
      </div>
    </div>
  );
}

interface Layer2RowProps {
  layer2: TreeNode;
  /** Index of this row within the hook's nav grid — must match the order
   *  in which the hook builds `grid` (which filters out empty layer-2s). */
  rowIndex: number;
  gridRow: number;
  gridCol: number;
  setGridPos: (row: number, col: number) => void;
  selectedSet: Set<string>;
  /** Owning class id, forwarded to onSelectLeaf so the hook can resolve the full
   *  (unfiltered) class. undefined for the single-class (flat) shape. */
  classFolderId?: string;
  /** Dim the row when the class is set to all-normal (still selectable — picking
   *  a pill overrides all-normal). */
  muted: boolean;
  onSelectLeaf: (leaf: TreeNode, classFolderId?: string) => void;
  /** Receives the DOM node of the currently-focused pill so the parent can
   *  scroll it into view when the keyboard cursor moves. */
  focusedPillRef: MutableRefObject<HTMLButtonElement | null>;
}

function Layer2Row({
  layer2,
  rowIndex,
  gridRow,
  gridCol,
  setGridPos,
  selectedSet,
  classFolderId,
  muted,
  onSelectLeaf,
  focusedPillRef,
}: Layer2RowProps) {
  const rowClasses = [styles.row, muted && styles.muted]
    .filter(Boolean)
    .join(' ');
  return (
    <div className={rowClasses}>
      <span className={styles.rowLabel}>{layer2.value}</span>
      <div className={styles.pills}>
        {layer2.children.map((leaf, colIndex) => {
          const isSelected = selectedSet.has(leaf.displayValue!.trim());
          const isFocused = rowIndex === gridRow && colIndex === gridCol;
          const classes = [
            styles.pill,
            isSelected && styles.pillSelected,
            isFocused && styles.pillFocused,
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={leaf.id}
              ref={isFocused ? focusedPillRef : undefined}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={classes}
              style={
                { '--pill-color': degreeColor(leaf.degree) } as CSSProperties
              }
              onMouseEnter={() => setGridPos(rowIndex, colIndex)}
              onClick={() => onSelectLeaf(leaf, classFolderId)}
            >
              {leaf.value}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const FindingGridPicker: FC<FindingGridPickerProps> = ({
  visibleOptions,
  gridRow,
  gridCol,
  setGridPos,
  selectedStatements,
  onSelectLeaf,
  onToggleAllNormal,
  focusedPillDomRef,
}) => {
  const sel = useMemo(() => selectedStatements ?? [], [selectedStatements]);
  const selectedSet = useMemo(() => new Set(sel.map((s) => s.trim())), [sel]);

  // Auto-scroll the focused pill into view on keyboard nav. `block: 'nearest'`
  // is a no-op when the pill is already visible, so mouse-hover updates to
  // `gridRow/gridCol` don't cause jumps — only off-screen targets scroll.
  // Also mirrors the DOM node into the hook's `focusedPillDomRef` so
  // wrap-aware arrow navigation can consult sibling geometry.
  const focusedPillRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (focusedPillDomRef) {
      // eslint-disable-next-line no-param-reassign
      focusedPillDomRef.current = focusedPillRef.current;
    }
    focusedPillRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [gridRow, gridCol, focusedPillDomRef, visibleOptions]);

  // Detect shape: top-level node whose first child is a leaf → layer-2 row.
  // Whose first child is a parent → class folder wrapping layer-2 rows.
  const isClassFolderShape = visibleOptions.some(
    (n) => n.children.length > 0 && !n.children[0].isLeaf
  );

  // Match the hook's grid layout exactly: traverse in the same order it
  // does, skipping layer-2 nodes with no children (empty rows). rowIndex
  // increments only when a renderable row is emitted.
  let cursor = 0;
  const nextRowIndex = () => {
    const i = cursor;
    cursor += 1;
    return i;
  };

  if (!isClassFolderShape) {
    // Single finding class — one All Normal at the top. Summary is the class's
    // dedicated "Normal" statement (rides along on every row as allNormalText).
    const classRows = visibleOptions.filter((r) => r.children.length > 0);
    const summary = classRows[0]?.allNormalText;
    const allNormal = isAllNormalActive(sel, summary);
    // The All Normal button is the first nav row of the class (matches the hook's
    // grid), so it consumes a row index before the attribute rows.
    const allNormalRow = classRows.length > 0 ? nextRowIndex() : -1;
    return (
      <div className={styles.grid}>
        {classRows.length > 0 && (
          <AllNormalButton
            active={allNormal}
            focused={gridRow === allNormalRow && gridCol === 0}
            rowIndex={allNormalRow}
            setGridPos={setGridPos}
            onToggle={() => onToggleAllNormal(undefined)}
            focusedPillRef={focusedPillRef}
          />
        )}
        {classRows.map((layer2) => (
          <Layer2Row
            key={layer2.id}
            layer2={layer2}
            rowIndex={nextRowIndex()}
            gridRow={gridRow}
            gridCol={gridCol}
            setGridPos={setGridPos}
            selectedSet={selectedSet}
            classFolderId={undefined}
            muted={allNormal}
            onSelectLeaf={onSelectLeaf}
            focusedPillRef={focusedPillRef}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.grid}>
      {visibleOptions.map((classFolder) => {
        // Each class's summary is its own dedicated "Normal" statement text, so
        // the all-normal state of every class in the group stays distinguishable.
        const classRows = classFolder.children.filter(
          (r) => r.children.length > 0
        );
        const summary = classRows[0]?.allNormalText;
        const allNormal = isAllNormalActive(sel, summary);
        const allNormalRow = classRows.length > 0 ? nextRowIndex() : -1;
        return (
          <div key={classFolder.id} className={styles.classSection}>
            <h4 className={styles.classHeader}>{classFolder.value}</h4>
            {classRows.length > 0 && (
              <AllNormalButton
                active={allNormal}
                focused={gridRow === allNormalRow && gridCol === 0}
                rowIndex={allNormalRow}
                setGridPos={setGridPos}
                onToggle={() => onToggleAllNormal(classFolder.id)}
                focusedPillRef={focusedPillRef}
              />
            )}
            {classRows.map((layer2) => (
              <Layer2Row
                key={layer2.id}
                layer2={layer2}
                rowIndex={nextRowIndex()}
                gridRow={gridRow}
                gridCol={gridCol}
                setGridPos={setGridPos}
                selectedSet={selectedSet}
                classFolderId={classFolder.id}
                muted={allNormal}
                onSelectLeaf={onSelectLeaf}
                focusedPillRef={focusedPillRef}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default FindingGridPicker;
